import React, { useEffect, useState } from 'react';
import { Drawer, Button, Box, Tooltip, BoxProps } from '@mui/material';
import { faRobot, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColorPalette } from '../config';
import { ChatComponent } from './chat';
import { useShortcut } from '../../hooks/use-shortcut';
import useChatCitizenContext from './hooks/use-chat-citizen-context';
import { useAiAssistantVisibility } from '../../hooks/ai-search/use-ai-assistant-visibility';

export interface IMessageCenterProps {
  sx?: BoxProps['sx'];
}
export function ChatDrawer(props: IMessageCenterProps) {
  const { sx } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  useShortcut({
    ctrlKey: true,
    shortcutKey: '½',
    onShortcut: () => {
      if (isOpen) {
        handleClose();
      } else {
        handleOpen();
      }
    },
  });


  const { isFasitAssistantVisible, isCitizenAssistantVisible } = useAiAssistantVisibility();
  const { citizenId } = useChatCitizenContext();

  const isDrawerVisible = isFasitAssistantVisible || isCitizenAssistantVisible;
  const isCitizenAssistantOnly = !isFasitAssistantVisible && isCitizenAssistantVisible;
  const isFasitAssistantOnly = isFasitAssistantVisible && !isCitizenAssistantVisible;
  const isDrawerButtonDisabled = isCitizenAssistantOnly && !citizenId;

  const assistantName = isFasitAssistantOnly ? 'Lexi' : isCitizenAssistantOnly ? 'Summa' : 'Fasit assistent';

  const isChatBotOpenStorageKey = 'FASIT_ISCHATDRAWEROPEN_KEY';
  const chatBotTabIndexKey = 'FASIT_CHATDRAWERTABINDEX_KEY';

  useEffect(() => {
    if (isDrawerButtonDisabled) {
      setIsOpen(false);
      return;
    }
    const openOnLoad = sessionStorage.getItem(isChatBotOpenStorageKey);
    const tabIndexOnLoad = sessionStorage.getItem(chatBotTabIndexKey);
    if (openOnLoad) {
      setIsOpen(true);
      setTabIndex(tabIndexOnLoad ? Number(tabIndexOnLoad) : 0);
    }
  }, [isDrawerButtonDisabled]);

  function handleClose() {
    sessionStorage.removeItem(isChatBotOpenStorageKey);
    sessionStorage.removeItem(chatBotTabIndexKey);
    setTabIndex(0);
    setIsOpen(false);
  }

  function handleOpen() {
    if (isDrawerButtonDisabled) return;
    sessionStorage.setItem(isChatBotOpenStorageKey, 'true');
    sessionStorage.setItem(chatBotTabIndexKey, '0');
    setTabIndex(0);
    setIsOpen(true);
  }

  function handleTabChange(val: number) {
    sessionStorage.setItem(chatBotTabIndexKey, val.toString());
    setTabIndex(val);
  }

  const DrawerComponent = (
    <>
      <Tooltip title={isDrawerButtonDisabled ? `Åbn en borger for at bruge ${assistantName}` : `${assistantName} (ctrl+½)`} placement="left" disableInteractive>
        <Button size="large" aria-label={`Åbn ${assistantName}`} onClick={handleOpen} sx={{ ...sx, color: ColorPalette.WHITE, cursor: isDrawerButtonDisabled ? 'default' : 'pointer', opacity: isDrawerButtonDisabled ? 0.4 : 1 }}>
          <FontAwesomeIcon icon={faRobot} />
        </Button>
      </Tooltip>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => handleClose()}
        variant="persistent"
        PaperProps={{
          sx: { width: 850 },
        }}
      >
        <Button
          sx={{
            border: 'none',
            borderBottom: `1px solid ${ColorPalette.LightAsteroidDust}`,
            cursor: 'pointer',
            display: 'flex',
            outline: 'none',
            textAlign: 'left',
            px: 2,
            height: 50,
            justifyContent: 'left',
            '&:hover': {
              backgroundColor: ColorPalette.LightAsteroidDust,
            },
            '&:focus': {
              backgroundColor: ColorPalette.LightAsteroidDust,
            },
          }}
          onClick={(e: any) => handleClose()}
          title={`Luk ${assistantName}`}
          aria-label={`Luk ${assistantName}`}
        >
          <FontAwesomeIcon icon={faChevronRight} /> <Box sx={{ display: 'inline-block', ml: 1 }}>{`Luk ${assistantName}`}</Box>
        </Button>

        <Box sx={{ height: 'calc(100% - 50px)', overflowY: 'auto', textAlign: 'left' }} onKeyDown={e => e.key === 'Escape' && handleClose()}>
          {
            // Workaround to set focus on the chat input field}
            isOpen && <ChatComponent tabIndex={tabIndex} onTabChange={handleTabChange} height="100%" />
          }
        </Box>
      </Drawer>
    </>
  );

  return isDrawerVisible ? DrawerComponent : <span />;
}
